import React, { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import CloseIcon from '@mui/icons-material/Close';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CircularProgress from '@mui/material/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  chip: {
    backgroundColor: '#EBF8FF',
    border: '1px solid #BEE3F8',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#E6F3FF',
    },
    '& .MuiChip-label': {
      color: '#2B6CB0',
      fontWeight: 500,
    },
    '& .MuiChip-deleteIcon': {
      color: '#3182CE',
      '&:hover': {
        color: '#2B6CB0',
      },
    },
  },
  chipNoDelete: {
    '& .MuiChip-deleteIcon': {
      display: 'none',
    },
  },
  editableChip: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1.5),
    backgroundColor: '#EBF8FF',
    border: '1px solid #3182CE',
    borderRadius: 16,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  teamSelect: {
    minWidth: 200,
    '& .MuiInputBase-root': {
      fontSize: 13,
      backgroundColor: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#CBD5E0',
    },
  },
  closeButton: {
    padding: 2,
    color: '#4A5568',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.05)',
    },
  },
  closeIcon: {
    fontSize: 16,
  },
  addButton: {
    backgroundColor: '#F7FAFC',
    border: '1px dashed #CBD5E0',
    borderRadius: 16,
    padding: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: '#EDF2F7',
      borderColor: '#A0AEC0',
    },
  },
  addIcon: {
    fontSize: 18,
    color: '#4A5568',
  },
  dropdownOption: {
    padding: theme.spacing(0.75, 1.5),
    margin: theme.spacing(0.25, 0.5),
    borderRadius: 4,
    fontSize: 13,
    fontFamily: 'monospace',
    '&:hover': {
      backgroundColor: '#EDF2F7',
    },
    '&[aria-selected="true"]': {
      backgroundColor: '#EBF8FF',
    },
  },
  disabledOption: {
    color: '#A0AEC0',
    fontStyle: 'italic',
  },
  dropdownPaper: {
    marginTop: 4,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    border: '1px solid #E2E8F0',
    borderRadius: 6,
  },
  dropdownListbox: {
    padding: theme.spacing(0.5, 0),
  },
  disabledBadge: {
    fontSize: 10,
    color: '#718096',
    marginLeft: theme.spacing(1),
  },
  selectedChip: {
    marginRight: 2,
    marginBottom: 2,
    height: 22,
    fontSize: 12,
  },
}));

interface EditableTeamSelectorProps {
  selectedTeams: string[];
  availableTeams: string[];
  isLoading: boolean;
  isTeamDisabled: (teamName: string) => boolean;
  onSave: (teamNames: string[]) => void;
  onCancel: () => void;
  onRemove?: () => void;
  isNew: boolean;
}

const EditableTeamSelector: React.FC<EditableTeamSelectorProps> = ({
  selectedTeams,
  availableTeams,
  isLoading,
  isTeamDisabled,
  onSave,
  onCancel,
  onRemove,
  isNew,
}) => {
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const [localSelected, setLocalSelected] = useState<string[]>(selectedTeams);

  const formatTeamName = (name: string) => {
    return name.replace('acl-dev-team-', '');
  };

  const handleChange = (newValue: string[]) => {
    // Ensure at least one team is selected
    if (newValue.length === 0) return;
    setLocalSelected(newValue);
  };

  const handleClickAway = () => {
    if (localSelected.length > 0) {
      onSave(localSelected);
    } else {
      onCancel();
    }
  };

  // Sort alphabetically by formatted name
  const sortedOptions = [...availableTeams].sort((a, b) =>
    formatTeamName(a).localeCompare(formatTeamName(b))
  );

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Paper className={classes.editableChip} ref={containerRef} elevation={0}>
        <Autocomplete
          multiple
          size="small"
          className={classes.teamSelect}
          options={sortedOptions}
          value={localSelected}
          loading={isLoading}
          onChange={(_, newValue) => handleChange(newValue)}
          getOptionDisabled={(option) => isTeamDisabled(option)}
          disableClearable
          disableCloseOnSelect
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={localSelected.length === 0 ? "Select teams..." : ""}
              variant="outlined"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          getOptionLabel={(option) => formatTeamName(option)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={formatTeamName(option)}
                size="small"
                className={classes.selectedChip}
                onDelete={value.length > 1 ? getTagProps({ index }).onDelete : undefined}
              />
            ))
          }
          renderOption={(props, option, { selected }) => {
            const disabled = isTeamDisabled(option);
            return (
              <li {...props} className={`${classes.dropdownOption} ${disabled ? classes.disabledOption : ''}`}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  disabled={disabled}
                />
                {formatTeamName(option)}
                {disabled && <span className={classes.disabledBadge}>dedicated page</span>}
              </li>
            );
          }}
          slotProps={{
            paper: { className: classes.dropdownPaper },
          }}
          ListboxProps={{ className: classes.dropdownListbox }}
          openOnFocus
          autoHighlight
        />
        <IconButton
          className={classes.closeButton}
          size="small"
          onClick={() => {
            if (onRemove && !isNew) {
              onRemove();
            } else {
              onCancel();
            }
          }}
        >
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </Paper>
    </ClickAwayListener>
  );
};

interface TeamSelectorProps {
  selectedTeams: string[];
  availableTeams: string[];
  isLoading: boolean;
  onAddTeam: (teamName: string) => void;
  onRemoveTeam: (teamName: string) => void;
  canRemoveTeam: (teamName: string) => boolean;
  isTeamDisabled: (teamName: string) => boolean;
  onSetTeams?: (teamNames: string[]) => void;
}

export const TeamSelector: React.FC<TeamSelectorProps> = ({
  selectedTeams,
  availableTeams,
  isLoading,
  onAddTeam,
  onRemoveTeam,
  canRemoveTeam: _canRemoveTeam,
  isTeamDisabled,
  onSetTeams,
}) => {
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState(false);

  const formatTeamName = (name: string) => {
    return name.replace('acl-dev-team-', '');
  };

  const formatChipLabel = (): string => {
    if (selectedTeams.length === 1) {
      return `Team = ${formatTeamName(selectedTeams[0])}`;
    }
    return `Team = (${selectedTeams.length})`;
  };

  const handleChipClick = () => {
    setIsEditing(true);
  };

  const handleSaveTeams = (teamNames: string[]) => {
    if (onSetTeams) {
      onSetTeams(teamNames);
    } else {
      // Fallback: add new teams, remove old ones
      const toAdd = teamNames.filter(t => !selectedTeams.includes(t));
      const toRemove = selectedTeams.filter(t => !teamNames.includes(t));
      toAdd.forEach(t => onAddTeam(t));
      toRemove.forEach(t => onRemoveTeam(t));
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Box className={classes.container}>
        <EditableTeamSelector
          selectedTeams={selectedTeams}
          availableTeams={availableTeams}
          isLoading={isLoading}
          isTeamDisabled={isTeamDisabled}
          onSave={handleSaveTeams}
          onCancel={handleCancelEdit}
          isNew={false}
        />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Tooltip
        title={selectedTeams.length > 1 ? selectedTeams.map(formatTeamName).join(', ') : 'Click to edit teams'}
      >
        <Chip
          className={classes.chip}
          label={formatChipLabel()}
          onClick={handleChipClick}
          size="small"
        />
      </Tooltip>
    </Box>
  );
};

export default TeamSelector;
